import React, { ReactNode, useCallback, useState } from "react";
import styled from "styled-components";
import {
  Button,
  Icon,
  IconName,
  Theme,
  colors,
} from "@literati/public-ui-shared-react";
import {
  UseMultilineEllipseOptions,
  useMultilineEllipse,
} from "utils/hooks/useMultilineEllipse";
import analytics from "analytics";

export type BookShuffleExpandableTextHeight = `${number}rem` | "auto";

type GetCollapsedHeight = Pick<
  UseMultilineEllipseOptions,
  "lineClamp" | "lineHeight"
>;

function getCollapsedHeight({
  lineClamp,
  lineHeight,
}: GetCollapsedHeight): BookShuffleExpandableTextHeight {
  return `${Number(lineHeight?.split("rem")[0]) * lineClamp}rem`;
}

/** This is the className that will be pre-pended to all of BookShuffleExpandableText's inner component classNames */
export const BOOK_SHUFFLE_EXPANDABLE_TEXT_CLASS_BASE =
  "book-shuffle-expandable-text";

/** These are all of the internal classNames that are used within the BookShuffleExpandableText component */
export const BookShuffleExpandableTextClassName = {
  BASE: BOOK_SHUFFLE_EXPANDABLE_TEXT_CLASS_BASE,
} as const;

const Text = styled.p<{
  $collapsedHeight: BookShuffleExpandableTextHeight;
  $expanded: boolean;
  $height: BookShuffleExpandableTextHeight;
  $truncateCss: string;
}>`
  height: ${({ $height }) => $height};
  text-overflow: ellipses;
  line-height: 1.5rem;

  ${({ $collapsedHeight, $expanded, $truncateCss }) =>
    !$expanded &&
    `
      ${$truncateCss}
      min-height: ${$collapsedHeight};
    `};
`;

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 1rem 0 0;
`;

const IconRotationWrapper = styled.div`
  transform: rotate(90deg);
`;

const StyledIcon = styled(Icon)<{ $isExpanded: boolean }>`
  transition: transform 400ms;
  transform: rotateY(${({ $isExpanded }) => $isExpanded && "180deg"});

  path {
    stroke: ${colors.tealShade2};
  }
`;

enum ButtonText {
  READ_LESS = "Read Less",
  READ_MORE = "Read More",
}

export interface BookShuffleExpandableTextProps
  extends UseMultilineEllipseOptions {
  className?: string;
  children: ReactNode;
}

export const BookShuffleExpandableText = ({
  children,
  className = "",
  lineClamp,
  lineHeight,
}: BookShuffleExpandableTextProps) => {
  const getCollapsedHeightCallback = useCallback(
    () => getCollapsedHeight({ lineClamp, lineHeight }),
    [lineClamp, lineHeight]
  );
  const collapsedHeight = getCollapsedHeightCallback();

  const [state, setState] = useState({
    buttonText: ButtonText.READ_LESS,
    expanded: true,
    height: collapsedHeight,
  });

  const { expanded } = state;

  const { isMinElementHeight, ref, showingEllipsis, truncateMultilineTextCss } =
    useMultilineEllipse<HTMLParagraphElement>({
      forceStateUpdateToggle: expanded,
      lineClamp,
      lineHeight,
    });

  const showExpandCollapseButton =
    (expanded && !isMinElementHeight) || (!expanded && showingEllipsis);

  const handleClick = () => {
    analytics.trackAmplitudeClickEvent(state.buttonText);

    setState((prevState) => ({
      buttonText:
        prevState.buttonText === ButtonText.READ_MORE
          ? ButtonText.READ_LESS
          : ButtonText.READ_MORE,
      expanded: !prevState.expanded,
      height: prevState.expanded ? collapsedHeight : "auto",
    }));
  };

  return (
    <Theme>
      <>
        <Text
          className={`${BookShuffleExpandableTextClassName.BASE} ${className}`}
          $collapsedHeight={collapsedHeight}
          $expanded={expanded}
          $height={state.height}
          $truncateCss={truncateMultilineTextCss}
          ref={ref}
        >
          {children}
        </Text>
        {showExpandCollapseButton && (
          <StyledButton onClick={handleClick} variant="link">
            {state.buttonText}
            <IconRotationWrapper>
              <StyledIcon
                name={IconName.CHEVRON_RIGHT}
                $isExpanded={expanded}
              />
            </IconRotationWrapper>
          </StyledButton>
        )}
      </>
    </Theme>
  );
};

export default BookShuffleExpandableText;
